import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Organization } from '../../../shared/interfaces/organization.interface';
import { FEATURE_FLAGS } from '../../../shared/constants/feature-flags';
import {
  DEFAULT_TEMPERATURE_THRESHOLDS,
  DEFAULT_WBGT_THRESHOLDS,
  normalizeTemperatureThresholds,
  normalizeWbgtThresholds,
  validateTemperatureThresholdOrder,
  validateWbgtThresholdOrder
} from '../../../shared/utils/sensor-status';

export interface OrganizationFormDialogData {
  organization?: Organization;
}

function organizationThresholdsValidator(control: AbstractControl): ValidationErrors | null {
  const group = control.get('wbgtThresholds');
  const thresholds = normalizeWbgtThresholds(group?.value);
  const temperatureGroup = control.get('temperatureThresholds');
  const temperatureThresholds = normalizeTemperatureThresholds(temperatureGroup?.value);
  const errors = {
    ...(validateWbgtThresholdOrder(thresholds) ? {} : { wbgtThresholdOrder: true }),
    ...(validateTemperatureThresholdOrder(temperatureThresholds) ? {} : { temperatureThresholdOrder: true }),
  };
  return Object.keys(errors).length > 0 ? errors : null;
}

@Component({
  selector: 'app-organization-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
  ],
  templateUrl: './organization-form.component.html',
  styleUrl: './organization-form.component.scss'
})
export class OrganizationFormComponent {
  private readonly dialogRef = inject(MatDialogRef<OrganizationFormComponent>);
  private readonly data = inject<OrganizationFormDialogData>(MAT_DIALOG_DATA, { optional: true });
  private readonly fb = inject(FormBuilder);

  public readonly isEditMode = !!this.data?.organization;
  public readonly isLoading = signal(false);
  public readonly featureFlags = FEATURE_FLAGS;
  public readonly defaultWbgtThresholds = DEFAULT_WBGT_THRESHOLDS;
  public readonly defaultTemperatureThresholds = DEFAULT_TEMPERATURE_THRESHOLDS;

  public readonly form: FormGroup = this.fb.group({
    name: [this.data?.organization?.name || '', [Validators.required]],
    wbgtThresholds: this.fb.group({
      almostSafe: [this.data?.organization?.wbgtThresholds?.almostSafe ?? null],
      caution: [this.data?.organization?.wbgtThresholds?.caution ?? null],
      warning: [this.data?.organization?.wbgtThresholds?.warning ?? null],
      severeWarning: [this.data?.organization?.wbgtThresholds?.severeWarning ?? null],
      danger: [this.data?.organization?.wbgtThresholds?.danger ?? null],
    }),
    temperatureThresholds: this.fb.group({
      freezingDanger: [this.data?.organization?.temperatureThresholds?.freezingDanger ?? null],
      freezingCaution: [this.data?.organization?.temperatureThresholds?.freezingCaution ?? null],
      coldCaution: [this.data?.organization?.temperatureThresholds?.coldCaution ?? null],
      mild: [this.data?.organization?.temperatureThresholds?.mild ?? null],
      tenOrMore: [this.data?.organization?.temperatureThresholds?.tenOrMore ?? null],
      fifteenOrMore: [this.data?.organization?.temperatureThresholds?.fifteenOrMore ?? null],
      twentyOrMore: [this.data?.organization?.temperatureThresholds?.twentyOrMore ?? null],
      twentyFiveOrMore: [this.data?.organization?.temperatureThresholds?.twentyFiveOrMore ?? null],
      thirtyOrMore: [this.data?.organization?.temperatureThresholds?.thirtyOrMore ?? null],
      thirtyFiveOrMore: [this.data?.organization?.temperatureThresholds?.thirtyFiveOrMore ?? null],
    }),
    ...Object.fromEntries(
      FEATURE_FLAGS.map(flag => [
        flag.id,
        [this.data?.organization?.enabledFeatures?.includes(flag.id) || false]
      ])
    )
  }, { validators: [organizationThresholdsValidator] });

  public onSubmit(): void {
    if (this.form.invalid) return;

    this.isLoading.set(true);
    try {
      const formValue = this.form.value;
      const enabledFeatures: string[] = FEATURE_FLAGS
        .filter(flag => formValue[flag.id])
        .map(flag => flag.id);

      const result: Partial<Organization> = {
        name: formValue.name,
        enabledFeatures,
      };
      result.wbgtThresholds = normalizeWbgtThresholds(formValue.wbgtThresholds) ?? null;
      result.temperatureThresholds = normalizeTemperatureThresholds(formValue.temperatureThresholds) ?? null;

      this.dialogRef.close(result);
    } finally {
      this.isLoading.set(false);
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
